/* TODO(ASIMO-2105): cherrera2@ to remove once experiment is over regardless of outcome */

import { type DataSourceConnectionDetails } from '../../connectedDataSource';
import { getItem, removeItem, setItem } from '../localStorage';

/** Default expiration time for third-party connection status cache (15 minutes in milliseconds) */
export const DEFAULT_EXPIRATION_FOR_3P_CONNECTION_STATUS = 1000 * 60 * 15; // 15 minutes in milliseconds

/**
 * Represents the cached status of a third-party connection with expiration
 */
export type ThirdPartyConnectionStatus = {
	/** Unix timestamp in milliseconds when this cache entry expires */
	expirationDate: number;
	/** Whether the user has completed OAuth authentication for this data source */
	isUserOAuthed: boolean;
	/** URL for initiating OAuth flow for this data source connection */
	outboundAuthUrl: string;
};

/** Represents the third party banner status */
export type ThirdPartyBannerStatus = {
	/** Whether the banner has been dismissed */
	hasBannerBeenDismissed: boolean;
};

/** Storage keys for persisting third party connection status in localStorage */
export const GOOGLE_DRIVE_CONNECTION_STATUS_STORAGE_KEY =
	'link-picker-google-drive-experiment-third-party-connection-status';
export const GOOGLE_DRIVE_CONNECTION_BANNER_STORAGE_KEY =
	'link-picker-google-drive-experiment-third-party-connection-banner';
export const ONE_DRIVE_CONNECTION_STATUS_STORAGE_KEY =
	'link-picker-onedrive-experiment-third-party-connection-status';
export const ONE_DRIVE_CONNECTION_BANNER_STORAGE_KEY =
	'link-picker-onedrive-experiment-third-party-connection-banner';

/**
 * Retrieves third party connection status from localStorage with expiration check
 * @returns The cached connection status, or null if not found or expired
 */
export const getThirdPartyConnectionStatus = (
	storageKey: string,
): ThirdPartyConnectionStatus | null => {
	const status = getItem<ThirdPartyConnectionStatus>(storageKey);
	// Check if the cached status has expired
	if (status && status.expirationDate <= Date.now()) {
		// Remove expired entry and return null
		removeItem(storageKey);
		return null;
	}
	return status;
};

/**
 * Stores third party connection status in localStorage with expiration timestamp
 * @param integrationDetails - The data source connection details from the server
 */
export const setThirdPartyConnectionStatus = (
	storageKey: string,
	integrationDetails: DataSourceConnectionDetails | undefined,
): void => {
	const { isUserOAuthed = false, outboundAuthUrl = '' } = integrationDetails ?? {};
	const status: ThirdPartyConnectionStatus = {
		// Set expiration time based on current time + default TTL
		expirationDate: Date.now() + DEFAULT_EXPIRATION_FOR_3P_CONNECTION_STATUS,
		isUserOAuthed,
		outboundAuthUrl,
	};
	setItem(storageKey, status);
};

/**
 * Gets third party connection banner status in localStorage
 * @returns The cached banner status, or null if not found
 */
export const getThirdPartyConnectionBannerStatus = (
	storageKey: string,
): ThirdPartyBannerStatus | null => {
	const status = getItem<ThirdPartyBannerStatus>(storageKey);
	return status ?? null;
};

/**
 * Stores third party connection banner status in localStorage
 * @param hasBannerBeenDismissed - Whether the banner has been dismissed
 */
export const setThirdPartyConnectionBanner = (
	storageKey: string,
	hasBannerBeenDismissed: boolean,
): void => {
	setItem(storageKey, { hasBannerBeenDismissed });
};

/**
 * Retrieves Google Drive connection status from localStorage with expiration check
 * @returns The cached connection status, or null if not found or expired
 */
export const getGoogleDriveConnectionStatus = (): ThirdPartyConnectionStatus | null =>
	getThirdPartyConnectionStatus(GOOGLE_DRIVE_CONNECTION_STATUS_STORAGE_KEY);

/**
 * Stores Google Drive connection status in localStorage with expiration timestamp
 * @param integrationDetails - The data source connection details from the server
 */
export const setGoogleDriveConnectionStatus = (
	integrationDetails: DataSourceConnectionDetails | undefined,
): void =>
	setThirdPartyConnectionStatus(GOOGLE_DRIVE_CONNECTION_STATUS_STORAGE_KEY, integrationDetails);

/**
 * Gets Google Drive connection banner status in localStorage
 * @returns The cached banner status, or null if not found
 */
export const getGoogleDriveConnectionBannerStatus = (): ThirdPartyBannerStatus | null =>
	getThirdPartyConnectionBannerStatus(GOOGLE_DRIVE_CONNECTION_BANNER_STORAGE_KEY);

/**
 * Stores Google Drive connection banner status in localStorage
 * @param hasBannerBeenDismissed - Whether the banner has been dismissed
 */
export const setGoogleDriveConnectionBanner = (hasBannerBeenDismissed: boolean): void =>
	setThirdPartyConnectionBanner(GOOGLE_DRIVE_CONNECTION_BANNER_STORAGE_KEY, hasBannerBeenDismissed);

/**
 * Retrieves OneDrive connection status from localStorage with expiration check
 * @returns The cached connection status, or null if not found or expired
 */
export const getOneDriveConnectionStatus = (): ThirdPartyConnectionStatus | null =>
	getThirdPartyConnectionStatus(ONE_DRIVE_CONNECTION_STATUS_STORAGE_KEY);

/**
 * Stores  OneDrive connection status in localStorage with expiration timestamp
 * @param integrationDetails - The data source connection details from the server
 */
export const setOneDriveConnectionStatus = (
	integrationDetails: DataSourceConnectionDetails | undefined,
): void =>
	setThirdPartyConnectionStatus(ONE_DRIVE_CONNECTION_STATUS_STORAGE_KEY, integrationDetails);

/**
 * Gets OneDrive connection banner status in localStorage
 * @returns The cached banner status, or null if not found
 */
export const getOneDriveConnectionBannerStatus = (): ThirdPartyBannerStatus | null =>
	getThirdPartyConnectionBannerStatus(ONE_DRIVE_CONNECTION_BANNER_STORAGE_KEY);

/**
 * Stores OneDrive connection banner status in localStorage
 * @param hasBannerBeenDismissed - Whether the banner has been dismissed
 */
export const setOneDriveConnectionBanner = (hasBannerBeenDismissed: boolean): void =>
	setThirdPartyConnectionBanner(ONE_DRIVE_CONNECTION_BANNER_STORAGE_KEY, hasBannerBeenDismissed);
